import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cn } from '@/lib/utils';
import { applyAnalyticsConsent } from '@/lib/analytics';

const CONSENT_KEY = 'hudan-cookie-consent';

export interface CookieConsent {
  version: 1;
  timestamp: string;
  essential: true;
  analytics: boolean;
  marketing: boolean;
}

function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (parsed.version !== 1) return null;
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}

function writeConsent(analytics: boolean, marketing: boolean): CookieConsent {
  const consent: CookieConsent = {
    version: 1,
    timestamp: new Date().toISOString(),
    essential: true,
    analytics,
    marketing,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
  return consent;
}

// TODO(sentry): gate Sentry.init behind analytics consent in a follow-up.

export function useCookieConsent() {
  const [consent, setConsentState] = useState<CookieConsent | null>(readConsent);

  const set = useCallback((analytics: boolean, marketing: boolean) => {
    const next = writeConsent(analytics, marketing);
    applyAnalyticsConsent(analytics, marketing);
    setConsentState(next);
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setConsentState(null);
  }, []);

  return { consent, set, clear };
}

const CookieBanner: React.FC = () => {
  const { t } = useLanguage();
  const prefersReducedMotion = useReducedMotion();

  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const stored = readConsent();
    if (stored === null) {
      applyAnalyticsConsent(false, false);
      setVisible(true);
    } else {
      applyAnalyticsConsent(stored.analytics, stored.marketing);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    writeConsent(true, true);
    applyAnalyticsConsent(true, true);
    setVisible(false);
  }, []);

  const handleRejectNonEssential = useCallback(() => {
    writeConsent(false, false);
    applyAnalyticsConsent(false, false);
    setVisible(false);
  }, []);

  const handleSave = useCallback(() => {
    writeConsent(analyticsOn, marketingOn);
    applyAnalyticsConsent(analyticsOn, marketingOn);
    setVisible(false);
  }, [analyticsOn, marketingOn]);

  if (!mounted || !visible) return null;

  const bannerEl = (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className={cn(
        'fixed bottom-0 inset-x-0 z-50',
        !prefersReducedMotion && 'animate-cookie-slide-up',
      )}
    >
      <div className="bg-slate-900 dark:bg-slate-950 border-t border-amber-400/20 shadow-2xl">
        <div className="h-0.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500" />

        <div className="container py-4 sm:py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
            <div className="flex-1 min-w-0">
              <p id="cookie-banner-title" className="text-white font-semibold text-sm mb-1">
                {t('cookies.title')}
              </p>
              <p id="cookie-banner-desc" className="text-gray-400 text-sm leading-relaxed">
                {t('cookies.message')}{' '}
                <Link
                  to="/privacy"
                  className="text-amber-400 hover:text-amber-300 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
                >
                  {t('cookies.privacyPolicyLink')}
                </Link>
              </p>
            </div>

            <div className="flex flex-wrap gap-2 lg:flex-nowrap lg:shrink-0 lg:items-center">
              <button
                type="button"
                onClick={() => setShowCustomize((v) => !v)}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-300 border border-white/20 hover:border-amber-400/40 hover:text-amber-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                aria-expanded={showCustomize}
              >
                {t('cookies.customize')}
              </button>
              <button
                type="button"
                onClick={handleRejectNonEssential}
                className="px-4 py-2 rounded-md text-sm font-medium text-gray-300 border border-white/20 hover:border-amber-400/40 hover:text-amber-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
              >
                {t('cookies.rejectNonEssential')}
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="px-4 py-2 rounded-md text-sm font-medium bg-amber-400 text-slate-900 hover:bg-amber-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
              >
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>

          {showCustomize && (
            <div className="mt-4 border-t border-white/10 pt-4 grid gap-3 sm:grid-cols-3">
              <CategoryRow
                label={t('cookies.essential')}
                description={t('cookies.essentialDesc')}
                checked
                disabled
                id="cookie-essential"
                onChange={() => {}}
              />
              <CategoryRow
                label={t('cookies.analytics')}
                description={t('cookies.analyticsDesc')}
                checked={analyticsOn}
                disabled={false}
                id="cookie-analytics"
                onChange={setAnalyticsOn}
              />
              <CategoryRow
                label={t('cookies.marketing')}
                description={t('cookies.marketingDesc')}
                checked={marketingOn}
                disabled={false}
                id="cookie-marketing"
                onChange={setMarketingOn}
              />

              <div className="sm:col-span-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleSave}
                  className="px-5 py-2 rounded-md text-sm font-medium bg-amber-400 text-slate-900 hover:bg-amber-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                >
                  {t('cookies.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(bannerEl, document.body);
};

interface CategoryRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  id: string;
  onChange: (v: boolean) => void;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  label,
  description,
  checked,
  disabled,
  id,
  onChange,
}) => (
  <label
    htmlFor={id}
    className={cn(
      'flex items-start gap-3 p-3 rounded-lg border',
      disabled
        ? 'border-white/10 opacity-60 cursor-default'
        : 'border-white/10 hover:border-amber-400/30 transition-colors cursor-pointer select-none',
    )}
  >
    <input
      type="checkbox"
      id={id}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 rounded accent-amber-400 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
      aria-describedby={`${id}-desc`}
    />
    <span>
      <span className="text-white text-sm font-medium block">{label}</span>
      <span id={`${id}-desc`} className="text-gray-400 text-xs leading-relaxed">
        {description}
      </span>
    </span>
  </label>
);

export default CookieBanner;
